import type { MediaGalleryItem } from '@xxtract/ui';
import type { Facet, Suggesties } from '@xxtract/tabelfilters';
import { isAxiosError } from 'axios';

import apiClient from '@/lib/api-client';

/** DAM library display types shown in the type-filter (mockup dam.html, h11-1). Mirrors MEDIA_DISPLAY_TYPES on the BFF. */
export const MEDIA_LIBRARY_DISPLAY_TYPES = ['packshot', 'etiket', 'sds', 'marketing', 'overig'] as const;
export type MediaLibraryDisplayType = (typeof MEDIA_LIBRARY_DISPLAY_TYPES)[number];

/** One item in the DAM library grid (h11-1): a discovered media file enriched with its product/brand link. */
export interface MediaLibraryItem {
  /** Wanneer het bestand is toegevoegd, uit de mediaserver. De server stuurt dit al mee. */
  uploadDate?: string | null;
  id: string;
  gtin: string;
  targetMarket: string;
  productName: string;
  brand: string;
  brandId: string | null;
  /** The product-detail page's route id (cinXmlId) — null when the file has no known product link (h11-4). */
  productId: string | null;
  typeInfo: string | null;
  displayType: MediaLibraryDisplayType;
  fileName: string;
  sizeBytes: number | null;
  mimeType: string | null;
  width: number | null;
  height: number | null;
  /** Pre-resolved, browser-safe CDN url (used directly as `<img src>`); null for documents or an unresolvable item. */
  thumbnailUrl: string | null;
  /** Pre-resolved, browser-safe CDN url for the click-to-enlarge dialog; null for documents. */
  webUrl: string | null;
}

export interface MediaLibraryBrandFacet {
  brandId: string;
  name: string;
  count: number;
}

export interface MediaLibraryResponse {
  data: MediaLibraryItem[] | null;
  meta: {
    total: number;
    page: number;
    pageSize: number;
    degraded: boolean;
    brands: MediaLibraryBrandFacet[];
    /** Trapsgewijze keuzes per keuzelijstkolom, alleen als gevraagd (spec-tabelfilters §8). */
    facetten?: Record<string, Facet[]>;
    suggesties?: Suggesties;
    /** De set is bij de bron afgekapt: aantallen zijn onvolledig. */
    afgekapt?: boolean;
  };
}

export interface MediaLibraryParams {
  query?: string;
  brandId?: string;
  /** "Niet gekoppeld" facet (h11-4): false shows only unlinked files, true only linked ones. */
  linked?: boolean;
  /** Kolomfilters, sortering, facetten en suggesties uit de lijststand, kant-en-klaar als queryparameters. */
  kolommen?: Record<string, string>;
  /**
   * Klantnummer van de bibliotheek die getoond wordt. Een klant laat dit weg — de server
   * kent zijn nummer al. Een medewerker of beheerder heeft zelf geen klantnummer en moet
   * dus zeggen naar wiens bibliotheek hij kijkt.
   */
  gln?: string;
  page: number;
  pageSize: number;
}

/**
 * Fetches the customer's own DAM library (h11-1): media aggregated across every known
 * product, enriched with product name/brand, filtered/sorted/paginated server-side.
 * Tenant identity comes from the session for a customer; an employee viewing a customer's
 * library names that customer via `gln` (the server still rejects a customer who names
 * someone else's number).
 */
export async function getMediaLibrary(params: MediaLibraryParams): Promise<MediaLibraryResponse> {
  const { query, brandId, linked, kolommen, gln, page, pageSize } = params;
  const response = await apiClient.get<MediaLibraryResponse>('/media', {
    params: {
      ...kolommen,
      ...(query == null || query === '' ? {} : { query }),
      ...(brandId == null ? {} : { brandId }),
      ...(linked == null ? {} : { linked }),
      ...(gln == null ? {} : { gln }),
      page,
      pageSize,
    },
  });
  return response.data;
}

/** One media entry as returned by the h2-1 discovery route (see MediaService.discover). */
export interface DiscoveredMediaItem {
  id: string;
  typeInfo: string | null;
  thumbnailUrl: string | null;
  webUrl: string | null;
  originalUrl: string | null;
  fileName?: string | null;
  mimeType?: string | null;
  size?: number | null;
}

export interface MediaDiscoveryMeta {
  degraded: boolean;
  cached: boolean;
  total: number | null;
  etag: string | null;
}

export interface MediaDiscoveryResult {
  data: DiscoveredMediaItem[] | null;
  meta: MediaDiscoveryMeta;
}

export interface GtinMediaParams {
  gln: string;
  gtin: string;
  targetMarket?: string;
  typeInfo?: string;
}

/**
 * typeInfo values that are documents (not previewable images). SDS is delivered as a
 * PDF download link; the gallery never previews a PDF (adversarial L3 expectation frame).
 */
const DOCUMENT_TYPE_INFO = new Set(['SAFETY_DATA_SHEET']);

/**
 * Case-insensitive, mirroring the backend: the mediaserver's type column has a
 * case-insensitive collation and really does hold both `label` and `LABEL`, so a document
 * must not stop being a document over its casing.
 */
export function isDocumentTypeInfo(typeInfo: string | null | undefined): boolean {
  return typeInfo != null && DOCUMENT_TYPE_INFO.has(typeInfo.toUpperCase());
}

/**
 * Fetches internal (employee/admin) GTIN media from the h2-1 media module. Not
 * tenant-scoped by design, but the route stays auth + RBAC protected server-side.
 */
export async function getGtinMedia({ gln, gtin, targetMarket = '528', typeInfo }: GtinMediaParams): Promise<MediaDiscoveryResult> {
  const response = await apiClient.get<MediaDiscoveryResult>(
    `/media/internal/trade-items/${encodeURIComponent(gln)}/${encodeURIComponent(gtin)}/${encodeURIComponent(targetMarket)}`,
    typeInfo == null ? undefined : { params: { typeInfo } },
  );
  return response.data;
}

/**
 * One item as returned by the h2-4 customer product-media listing. Unlike
 * {@link DiscoveredMediaItem}, thumbnailUrl/webUrl are already resolved, browser-safe CDN
 * urls (no key) — the BFF resolves them server-side so the gallery never needs a Bearer
 * header on an `<img>` tag. Both are null for documents (SDS) or on a degraded item.
 */
export interface ResolvedProductMediaItem {
  id: string;
  typeInfo: string | null;
  thumbnailUrl: string | null;
  webUrl: string | null;
  /** Best-effort display file name (fidelity fix, mockup product-media.html); no longer just the mediaId. */
  fileName: string;
  sizeBytes: number | null;
  mimeType: string | null;
  width: number | null;
  height: number | null;
  /** Coarse display bucket for the type badge and filter tabs — same taxonomy as the DAM library. */
  displayType: MediaLibraryDisplayType;
}

export interface ProductMediaListing {
  data: ResolvedProductMediaItem[] | null;
  meta: MediaDiscoveryMeta;
}

/**
 * Fetches the customer's own product media (tenant-scoped server-side). Used by the
 * product-media page reached from the product-list thumbnail (FR14a).
 */
export async function getProductMedia({ gln, gtin, targetMarket = '528' }: GtinMediaParams): Promise<ProductMediaListing> {
  const response = await apiClient.get<ProductMediaListing>(
    `/media/products/${encodeURIComponent(gln)}/${encodeURIComponent(gtin)}/${encodeURIComponent(targetMarket)}`,
  );
  return response.data;
}

/** FR51 conversion matrix (h11-3): 3 output formats x 3 sizes, no free-form values. Mirrors CONVERT_FORMATS/CONVERT_SIZES on the BFF. */
/**
 * Een bovengrens op het ophalen van een bestand. Zonder deze grens blijft een verzoek dat nooit
 * terugkomt oneindig hangen, en dan draait de downloadknop eeuwig door — de gebruiker kan het
 * bestand dan niet eens opnieuw proberen zonder de pagina te herladen. Ruim genomen: grote
 * bestanden en bundels mogen lang duren.
 */
const MEDIA_DOWNLOAD_TIMEOUT_MS = 5 * 60 * 1000;

export const CONVERT_FORMATS = ['png', 'jpg', 'webp'] as const;
export type ConvertFormat = (typeof CONVERT_FORMATS)[number];
export const CONVERT_SIZES = ['original', '1200', '800'] as const;
export type ConvertSize = (typeof CONVERT_SIZES)[number];
export interface ConvertOptions {
  format: ConvertFormat;
  size: ConvertSize;
}

/** Thrown when the server refuses a ZIP selection that exceeds the 1.000 items / 500 MB ceiling. */
export class ZipLimitExceededError extends Error {
  constructor() {
    super('ZIP selection exceeds the mediaserver bulk limit');
    this.name = 'ZipLimitExceededError';
  }
}

export interface DownloadZipParams {
  gln: string;
  gtin: string;
  targetMarket?: string;
  mediaIds: string[];
}

/**
 * Requests a server-built ZIP (per-file, selection or "download all"). The ZIP is built by
 * the mediaserver bulk faciliteit — never client-side. A 413 becomes a typed limit error so
 * the page can show split advice.
 */
export async function downloadProductZip({ gln, gtin, targetMarket = '528', mediaIds }: DownloadZipParams): Promise<Blob> {
  try {
    const response = await apiClient.post('/media/download-zip', { gln, gtin, targetMarket, mediaIds }, { responseType: 'blob' });
    return response.data as Blob;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 413) throw new ZipLimitExceededError();
    throw error;
  }
}

/**
 * Requests a server-built ZIP of a DAM-library selection (h11-2), which may span multiple
 * products/GTINs — same path as {@link downloadProductZip} but without gln/gtin/targetMarket
 * in the body: tenant identity comes from the session, and the BFF streams its own ZIP when
 * the selection is not servable by the mediaserver's single-product bulk faciliteit
 * (MediaService.createLibraryArchive). A 413 becomes the same typed limit error so the page
 * can show the same split-selection advice as the product-media page.
 */
export async function downloadLibraryZip(mediaIds: string[], convert?: ConvertOptions, gln?: string): Promise<Blob> {
  try {
    const response = await apiClient.post('/media/download-zip', { mediaIds, ...(convert == null ? {} : { convert }), ...(gln == null ? {} : { gln }) }, { responseType: 'blob', timeout: MEDIA_DOWNLOAD_TIMEOUT_MS });
    return response.data as Blob;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 413) throw new ZipLimitExceededError();
    throw error;
  }
}

/** Triggers a browser download of a server-built blob without building anything client-side. */
export function triggerBlobDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/**
 * Downloads one media item via an authenticated (Bearer) blob fetch and triggers a real
 * "save as", instead of a plain `<a href>` — the per-file download route is same-origin
 * and Bearer-protected, so a native link cannot reach it (FR14a per-file download).
 */
/**
 * Het adres van de grote versie van één bibliotheekbestand, voor het vergrootvenster. Wordt pas
 * aangeroepen als iemand een bestand aanklikt — een lijst van honderd bestanden mag geen honderd
 * grote afbeeldingen ophalen. De server stelt hierbij vast dat het bestand van deze klant is.
 */
export async function fetchLibraryWebImage(mediaId: string, gln?: string): Promise<string | null> {
  const response = await apiClient.get<{ data: { url: string } }>(
    `/media/${encodeURIComponent(mediaId)}/web-image`,
    { params: gln == null ? {} : { gln } },
  );
  return response.data.data.url ?? null;
}

export async function downloadProductMediaItem({ gln, gtin, targetMarket = '528' }: GtinMediaParams, mediaId: string, fileName: string): Promise<void> {
  const response = await apiClient.get(
    `/media/products/${encodeURIComponent(gln)}/${encodeURIComponent(gtin)}/${encodeURIComponent(targetMarket)}/media/${encodeURIComponent(mediaId)}/download`,
    { responseType: 'blob', timeout: MEDIA_DOWNLOAD_TIMEOUT_MS },
  );
  triggerBlobDownload(response.data as Blob, fileName);
}

interface GalleryDescriptorResponse { data: { url: string; expiresAt: number } }

/**
 * h3-4: mints a short-lived signed delivery URL for ONE gallery mediaId (internal GTIN-detail
 * gallery). Unlike {@link redirectRoute}, the returned URL is directly usable as an `<img
 * src>` — the BFF route it points to needs no Authorization header (project-context
 * `ProductImage`/h2-1 descriptor-mint convention, generalized here to an arbitrary mediaId).
 */
export async function mintGtinGalleryDescriptor(params: GtinMediaParams, mediaId: string, variant: 'thumbnail' | 'web'): Promise<string> {
  const { gln, gtin, targetMarket = '528' } = params;
  const response = await apiClient.get<GalleryDescriptorResponse>(
    `/media/internal/trade-items/${encodeURIComponent(gln)}/${encodeURIComponent(gtin)}/${encodeURIComponent(targetMarket)}/media/${encodeURIComponent(mediaId)}/${variant}/descriptor`,
  );
  return response.data.data.url;
}

/**
 * Downloads one internal (employee/admin) GTIN media item via an authenticated blob fetch
 * (h3-4) — same convention as {@link downloadProductMediaItem}, but not tenant-scoped.
 */
export async function downloadGtinMediaItem(params: GtinMediaParams, mediaId: string, fileName: string): Promise<void> {
  const { gln, gtin, targetMarket = '528' } = params;
  const response = await apiClient.get(
    `/media/internal/trade-items/${encodeURIComponent(gln)}/${encodeURIComponent(gtin)}/${encodeURIComponent(targetMarket)}/media/${encodeURIComponent(mediaId)}/download`,
    { responseType: 'blob' },
  );
  triggerBlobDownload(response.data as Blob, fileName);
}

function redirectRoute(params: GtinMediaParams, mediaId: string, variant: 'thumbnail' | 'web' | 'original'): string {
  const { gln, gtin, targetMarket = '528' } = params;
  return `/api/v1/media/trade-items/${encodeURIComponent(gln)}/${encodeURIComponent(gtin)}/${encodeURIComponent(targetMarket)}/media/${encodeURIComponent(mediaId)}/${variant}`;
}

/**
 * Maps discovery items to presentational gallery items following the h2-1/h2-2 BFF
 * redirect-route convention. Browser-safe signed delivery is wired at GTIN-detail
 * integration (h3-4); this mapper only produces the logical BFF routes and the
 * image/document classification the gallery needs.
 */
export function toGalleryItems(items: DiscoveredMediaItem[], params: GtinMediaParams): MediaGalleryItem[] {
  return items.map((item) => {
    const typeInfo = item.typeInfo ?? 'UNKNOWN';
    const isDocument = isDocumentTypeInfo(typeInfo);
    const fileName = item.fileName ?? item.id;
    if (isDocument) {
      return {
        mediaId: item.id,
        typeInfo,
        kind: 'document' as const,
        fileName,
        downloadRoute: redirectRoute(params, item.id, 'original'),
        ...(item.mimeType == null ? {} : { mimeType: item.mimeType }),
        ...(item.size == null ? {} : { size: item.size }),
      };
    }
    return {
      mediaId: item.id,
      typeInfo,
      kind: 'image' as const,
      fileName,
      thumbnailRoute: redirectRoute(params, item.id, 'thumbnail'),
      imageRoute: redirectRoute(params, item.id, 'web'),
      downloadRoute: redirectRoute(params, item.id, 'original'),
      ...(item.mimeType == null ? {} : { mimeType: item.mimeType }),
      ...(item.size == null ? {} : { size: item.size }),
    };
  });
}

/**
 * Maps the h2-4 customer product-media listing to presentational gallery items. Unlike
 * {@link toGalleryItems}, thumbnail/web are already-resolved CDN urls (used directly as
 * `<img src>`, no BFF round trip needed); downloads always go through the gallery's
 * `onDownload` callback (an authenticated blob fetch), never a plain href, since the
 * per-file download route is Bearer-protected. `fileName`/`displayType`/size/mime/dimensions
 * are carried through for the per-tile metadata line and type badge (fidelity fix).
 */
export function toProductGalleryItems(items: ResolvedProductMediaItem[]): MediaGalleryItem[] {
  return items.map((item) => {
    const typeInfo = item.typeInfo ?? 'UNKNOWN';
    const isDocument = isDocumentTypeInfo(typeInfo);
    // Falls back to the id only when the listing itself has no usable name (defensive; the
    // BFF already derives one from the CDN url as a last resort — see MediaService).
    const fileName = item.fileName === '' ? item.id : item.fileName;
    const shared = {
      typeInfo,
      fileName,
      displayType: item.displayType,
      ...(item.mimeType == null ? {} : { mimeType: item.mimeType }),
      ...(item.sizeBytes == null ? {} : { size: item.sizeBytes }),
      ...(item.width == null ? {} : { width: item.width }),
      ...(item.height == null ? {} : { height: item.height }),
    };
    if (isDocument) return { mediaId: item.id, kind: 'document' as const, ...shared };
    return {
      mediaId: item.id,
      kind: 'image' as const,
      ...shared,
      ...(item.thumbnailUrl == null ? {} : { thumbnailRoute: item.thumbnailUrl }),
      ...(item.webUrl == null ? {} : { imageRoute: item.webUrl }),
    };
  });
}
